import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { KPICard, KPICardRow, StatusBadge } from "@/components/dashboard";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, AlertCircle, Clock, ShieldAlert, TrendingDown } from "lucide-react";

export default function SalviaGargalos() {
  usePageTitle("Gargalos");
  const { user } = useAuth();
  const { data, isLoading, error } = trpc.dashboards.salvia.bottlenecks.useQuery(undefined, {
    enabled: !!user && user.role === "admin",
    refetchOnWindowFocus: false,
  });

  if (error) {
    return (
      <SalviaLayout title="Dashboard — Gargalos Operacionais">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;

  return (
    <SalviaLayout title="Dashboard — Gargalos Operacionais">
      <div className="space-y-6">
        <KPICardRow>
          <KPICard
            label="Bloqueios"
            value={data?.blockages?.length ?? 0}
            icon={AlertCircle}
            iconColor="text-red-600"
            iconBg="bg-red-100"
            subtitle="Empresas com avaliações bloqueadas"
            loading={loading}
          />
          <KPICard
            label="Campanhas Vencidas"
            value={data?.staleCampaigns?.length ?? 0}
            icon={Clock}
            iconColor="text-orange-600"
            iconBg="bg-orange-100"
            subtitle="Campanhas ativas após prazo"
            loading={loading}
          />
          <KPICard
            label="Baixa Adesão"
            value={data?.lowAdesao?.length ?? 0}
            icon={TrendingDown}
            iconColor="text-amber-600"
            iconBg="bg-amber-100"
            subtitle="Campanhas com < 30% de adesão"
            loading={loading}
          />
        </KPICardRow>

        {/* Bloqueios por Empresa */}
        <Card className="p-6">
          <h3 className="text-sm font-semibold mb-4">Bloqueios por Empresa</h3>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map((i: number) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : !data?.blockages || data.blockages.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhum bloqueio identificado</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Empresa</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Canal</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Qtd</th>
                  </tr>
                </thead>
                <tbody>
                  {data.blockages.map((item: any, i: number) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2.5 font-medium">{item.companyName}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{item.channelName}</td>
                      <td className="px-3 py-2.5"><StatusBadge status={item.status} /></td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Campanhas Vencidas */}
        {data?.staleCampaigns && data.staleCampaigns.length > 0 && (
          <Card className="p-6">
            <h3 className="text-sm font-semibold mb-4">Campanhas Vencidas (Ativas após prazo)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Empresa</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Canal</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Dias Vencidos</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Respostas</th>
                  </tr>
                </thead>
                <tbody>
                  {data.staleCampaigns.map((item: any, i: number) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2.5 font-medium">{item.companyName}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{item.channelName}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-red-600 font-semibold">{item.daysOverdue}d</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{item.completed}/{item.totalVidas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Baixa Adesão */}
        {data?.lowAdesao && data.lowAdesao.length > 0 && (
          <Card className="p-6">
            <h3 className="text-sm font-semibold mb-4">Campanhas com Baixa Adesão (&lt; 30%)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Empresa</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Canal</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Respostas</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Taxa</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lowAdesao.map((item: any, i: number) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2.5 font-medium">{item.companyName}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{item.channelName}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{item.completed}/{item.totalVidas}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-amber-600 font-semibold">{item.taxa}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </SalviaLayout>
  );
}
